import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type SingleDeleteRecord = {
  id: string;
  number?: string;
  title?: string;
  apPayeeName?: string | null;
  totalAmount?: string | null;
};

type ConfirmDeleteModalContextType = {
  onOpenConfirmDeleteModal: (record: SingleDeleteRecord) => void;
  onCloseSingleDeleteModal: () => void;
  singleDeleteModalOpen: boolean;
  singleDeleteRecord: SingleDeleteRecord | null;
  refetch: () => Promise<unknown>;
  onDrawerOpen: (id: string) => void;
  onDrawerClose: () => void;
  selectedId: string | null;
};

const Context = createContext<ConfirmDeleteModalContextType>({
  onOpenConfirmDeleteModal: () => {},
  onCloseSingleDeleteModal: () => {},
  singleDeleteModalOpen: false,
  singleDeleteRecord: null,
  refetch: () => Promise.resolve(),
  onDrawerOpen: () => {},
  onDrawerClose: () => {},
  selectedId: null,
});

type ConfirmDeleteModalProviderProps = PropsWithChildren<{
  refetch: () => Promise<unknown>;
  onDrawerOpen: (id: string) => void;
  onDrawerClose: () => void;
  selectedId: string | null;
}>;

export const ConfirmDeleteModalProvider: FC<
  ConfirmDeleteModalProviderProps
> = ({ children, refetch, onDrawerOpen, onDrawerClose, selectedId }) => {
  const [singleDeleteRecord, setSingleDeleteRecord] =
    useState<SingleDeleteRecord | null>(null);
  const [singleDeleteModalOpen, setSingleDeleteModalOpen] =
    useState<boolean>(false);

  const onOpenConfirmDeleteModal = useCallback((record: SingleDeleteRecord) => {
    setSingleDeleteRecord(record);
    setSingleDeleteModalOpen(true);
  }, []);

  const onCloseSingleDeleteModal = useCallback(() => {
    setSingleDeleteModalOpen(false);
    setSingleDeleteRecord(null);
  }, []);

  const value = useMemo(
    () => ({
      onOpenConfirmDeleteModal,
      onCloseSingleDeleteModal,
      singleDeleteModalOpen,
      singleDeleteRecord,
      refetch,
      onDrawerOpen,
      onDrawerClose,
      selectedId,
    }),
    [
      onOpenConfirmDeleteModal,
      onCloseSingleDeleteModal,
      singleDeleteModalOpen,
      singleDeleteRecord,
      refetch,
      onDrawerOpen,
      onDrawerClose,
      selectedId,
    ]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

ConfirmDeleteModalProvider.displayName = 'ConfirmDeleteModalProvider';

export const useConfirmDeleteModal = () => useContext(Context);
